// Step 3 I2 (gate half): the card for a live summary of a collection on a website (neucleos_canvas
// dataset-aggregate-create; backend lib/gateCatalog.js AGGREGATE_DATASET_STEP_KEY, approved only on this card). It says,
// in plain words and only from the stored call's arguments (never request text): what it computes (the metric of the
// field), how it is grouped, which records it counts (the filter), that it covers EVERY matching record up to 1000 and
// not just one page, and what visitors see. A call it cannot show exactly cannot be approved here.

export const AGGREGATE_DATASET_ACTION = "dataset-aggregate-create";
/** The row cap the World computes a summary over (backend canvasCollections.js AGGREGATE_ROW_CAP). */
export const AGGREGATE_ROW_CAP = 1000;
const METRIC_WORDS: Readonly<Record<string, string>> = {
  count: "The number of records",
  sum: "The total of",
  avg: "The average of",
  min: "The smallest",
  max: "The largest",
};
export const AGGREGATE_CARD_BLOCKED = "This card cannot show this summary exactly, so it cannot be approved here.";

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" && value.length <= 200 ? value.trim() : null;
const quoted = (value: string) => `“${value}”`;

export interface AggregateDatasetCard {
  readonly headline: string;
  readonly rows: ReadonlyArray<readonly [label: string, text: string]>;
  readonly blocked: string | null;
}

/** The card's words for one stored call, or a blocked card when any part it must state is missing or unreadable. */
export function aggregateDatasetCard(record: Record<string, unknown>): AggregateDatasetCard {
  const aggregate = record["aggregate"];
  const named = aggregate && typeof aggregate === "object" && !Array.isArray(aggregate) ? (aggregate as Record<string, unknown>) : null;
  const metric = typeof named?.["metric"] === "string" ? named["metric"] : null;
  const field = named?.["field"] === undefined ? null : text(named["field"]);
  const groupBy = named?.["groupBy"] === undefined ? null : text(named["groupBy"]);
  const site = text(record["siteKey"]);
  const path = text(record["path"]);
  const collection = text(record["collectionId"]);
  const index = record["index"] === undefined ? null : text(record["index"]);
  const equals = record["equals"];
  const extra = named ? Object.keys(named).filter((key) => !["metric", "field", "groupBy"].includes(key)) : [];
  const unreadable =
    metric === null || !(metric in METRIC_WORDS) || (metric !== "count" && field === null) ||
    (named?.["field"] !== undefined && field === null) || (named?.["groupBy"] !== undefined && groupBy === null) ||
    site === null || path === null || collection === null || extra.length > 0 ||
    (record["index"] !== undefined && index === null) || (equals !== undefined && index === null) ||
    (equals !== undefined && !["string", "number", "boolean"].includes(typeof equals)) || record["limit"] !== undefined ||
    // cc-desk round 1: nothing it would not show (another member's records, an unreadable expiry, a field it cannot name).
    record["ownerActorId"] !== undefined || typeof record["expiresAt"] !== "number" || !Number.isFinite(record["expiresAt"]) ||
    !Array.isArray(record["fields"]) || (record["fields"] as unknown[]).length === 0 || (record["fields"] as unknown[]).some((one) => text(one) === null) ||
    text(record["datasetPurpose"]) === null;
  if (unreadable)
    return { headline: "Publish a live summary on a website", rows: [], blocked: AGGREGATE_CARD_BLOCKED };
  const computes = metric === "count" ? METRIC_WORDS["count"] : `${METRIC_WORDS[metric as string]} ${quoted(field as string)}`;
  const fields = record["fields"] as string[];
  // The same words as Date#toLocaleString (date and time), without constructing a Date (the shared package's Effect rule).
  const until = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }).format(record["expiresAt"] as number);
  const purpose = text(record["datasetPurpose"]) ?? "";
  const shownValue = typeof equals === "string" ? quoted(equals) : String(equals);
  return {
    headline: `Publish a live summary on ${site}${path}`,
    rows: [
      ["What it shows", `${computes}${groupBy === null ? "" : `, for each ${quoted(groupBy)}`}.`],
      ["Which records", `${index === null ? "Every record" : `Only records where ${quoted(index)} is ${shownValue}`} in collection ${collection} that you can read (in a collection where each person sees only their own records, only yours).`],
      ["How much", `It covers EVERY matching record, up to ${AGGREGATE_ROW_CAP}, not just what one page shows. Past that limit (or its size limits) visitors see no summary at all, never a partial one.`],
      ["Why", purpose],
      ["What visitors see", "Only this summary, not the records themselves. A group of one record, a smallest or a largest value can still show one record's value."],
      ["Fields it may read", fields.map(quoted).join(", ")],
      ["Live until", until],
    ],
    blocked: null,
  };
}
